import type {Language, ScheduledExport, Paging, ExportsSortBy} from '@/types'

import React from 'react'
import {connect} from 'react-redux'
import {map, mergeAll} from 'ramda'
import {portfoliosActions, uiActions, portfoliosSelectors, appSelectors} from '@/store'
import {date} from '@/format'
import DeleteButton from '@/controls/delete-button'
import EditButton from '@/controls/edit-button'
import NoAvailableTableData from '@/elements/no-available-table-data'
import Pagination from '@/controls/pagination'

type Props = {
  exports: Array<ScheduledExport>,
  l: Language,
  paging: Paging,
  sortBy: (ExportsSortBy, string) => void,
  pageChange: (string) => void,
  langCode: string,
}

const getClassNameForSortBy = ({sortOptions}) => (column: ExportsSortBy) => {
  let classNames = ['sort-by']
  const {sortBy, sortDir} = sortOptions
  if (sortBy === column) {
    if (sortDir === 'asc') {
      classNames.push('drop-up')
    } else {
      classNames.push('drop-down')
    }
  }
  return classNames.join(' ')
}

const renderRow = ({
  deleteExport,
  l,
  saveExport,
  setupEditExportForm,
  portfolios,
  langCode,
}) => (
  scheduledExport: ScheduledExport
) =>
  <tr key={scheduledExport.exportId}>
    <td width='5%'>
      <EditButton id='edit_export'
        title={'Edit export'}
        onClick={() => setupEditExportForm(scheduledExport.exportId)}
        onSave={() => saveExport(scheduledExport.exportId, scheduledExport.name)}
      />
    </td>
    <td width='5%'>
      <DeleteButton id='delete_export'
        onConfirm={() => deleteExport(scheduledExport.exportId, scheduledExport.name)}
        message={''}
      />
    </td>
    <td width='30%'>{scheduledExport.name}</td>
    <td width='20%'>{l(scheduledExport.exportType)}</td>
    <td width='20%' className='date-format'>{scheduledExport.lastExportDate && date(scheduledExport.lastExportDate, langCode)}</td>
    <td width='20%' className='date-format'>{scheduledExport.nextExportDate && date(scheduledExport.nextExportDate, langCode)}</td>
  </tr>

const renderRows = (props) => map(renderRow(props))

export const RawScheduledExports = (props: Props) =>
  <div className="monitoring-table-ctr">
    <h4>{props.l('Scheduled exports')}</h4>
    <table className={props.exports.length > 10 ? 'table table-condensed shrink' : 'table table-condensed'}>
      <thead>
        <tr className={props.exports.length === 0 ? 'disabled-header' : ''} height="48">
          <th width='5%'></th>
          <th width='5%'></th>
          <th width='30%'><a onClick={() => props.exports.length > 0 ? props.sortBy('name', 'scheduled') : null}>{props.l('Export Name')}<span className={getClassNameForSortBy(props)('name')}></span></a></th>
          <th width='20%'><a onClick={() => props.exports.length > 0 ? props.sortBy('exportType', 'scheduled') : null}>{props.l('Type')}<span className={getClassNameForSortBy(props)('exportType')}></span></a></th>
          <th width='20%'><a onClick={() => props.exports.length > 0 ? props.sortBy('lastModifiedDate', 'scheduled') : null}>{props.l('Last export')}<span className={getClassNameForSortBy(props)('lastModifiedDate')}></span></a></th>
          <th width='20%'><a onClick={() => props.exports.length > 0 ? props.sortBy('nextScheduleDate', 'scheduled') : null}>{props.l('Next export')}<span className={getClassNameForSortBy(props)('nextScheduleDate')}></span></a></th>
        </tr>
      </thead>
    </table>
    <div className="tbody-scroll">
      <table className="table table-condensed" style={{position: 'relative'}}>
        <tbody>
          {props.exports.length === 0
            ? <NoAvailableTableData numOfCols={8} text={props.l('There are no scheduled exports')}/>
            : renderRows(props)(props.exports)}
        </tbody>
      </table>
    </div>
    {
      props.scheduledExportsCount > props.paging.pageSize ? <Pagination
        activePage={props.paging.pageNumber + 1}
        pageSize={props.paging.pageSize}
        totalItems={props.scheduledExportsCount}
        onChange={props.pageChange('scheduled')}
      /> : false
    }
  </div>

const mapStateToProps = (state) => ({
  paging: state.ui.scheduledExportsPaging,
  sortOptions: state.ui.scheduledExportsSortOptions,
  scheduledExportsCount: portfoliosSelectors.getActivePortfolioScheduledExportsCount(state),
  l: appSelectors.getL(state),
  langCode: state.app.user.preferredLanguageCode,
})

export default connect(
  mapStateToProps,
  mergeAll([portfoliosActions, uiActions])
)(RawScheduledExports)



// WEBPACK FOOTER //
// ./src/views/exports-page/scheduled-exports.js